/**
 * useSchedulerApi Hook
 *
 * Centralized API layer for the scheduler module with:
 * - Granular loading states (initial, patrols, saving, deleting)
 * - Automatic retry with exponential backoff for network failures
 * - Unified error handling and user notifications
 * - Conflict detection integration
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import getSchedulesByDateRange from 'api/scheduler/getSchedulesByDateRange';
import createSinglePatrol from 'api/scheduler/createSinglePatrol';
import updateSinglePatrol from 'api/scheduler/updateSinglePatrol';
import deleteSinglePatrol from 'api/scheduler/deleteSinglePatrol';
import bulkCreatePatrols from 'api/scheduler/bulkCreatePatrols';
import { fetchGuards } from 'services/api/guardService';
import { fetchCompanies } from 'services/api/companyService';
import { detectBulkConflicts, formatConflictSummary } from 'utils/conflictDetection';
import { API_RETRY_COUNT, API_RETRY_DELAY_MS } from '../constants';

/**
 * Loading state shape for granular loading indicators
 * @typedef {Object} LoadingState
 * @property {boolean} initial - Initial data (guards, companies) loading
 * @property {boolean} patrols - Schedule/patrols loading
 * @property {boolean} saving - Saving patrol(s) in progress
 * @property {boolean} deleting - Deleting patrol in progress
 */

/**
 * Custom hook for scheduler API calls with centralized error handling.
 * Provides:
 * - Granular loading states for different operations
 * - Retry logic for transient network failures
 * - Proper error notifications and consistent error logging
 */
const useSchedulerApi = () => {
  const { notifyError, notifySuccess, notifyWarning } = useNotificationProvider();

  // Granular loading states for better UX feedback
  const [loadingState, setLoadingState] = useState({
    initial: false,
    patrols: false,
    saving: false,
    deleting: false,
  });

  // Legacy isLoading for backward compatibility (true if any operation is loading)
  const isLoading =
    loadingState.initial || loadingState.patrols || loadingState.saving || loadingState.deleting;

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Safely update a specific loading state
   * @param {string} key - Loading state key (initial, patrols, saving, deleting)
   * @param {boolean} value - Loading state value
   */
  const setLoading = useCallback((key, value) => {
    if (mountedRef.current) {
      setLoadingState((prev) => ({ ...prev, [key]: value }));
    }
  }, []);

  /**
   * Execute an async function with retry logic for transient failures.
   * Uses exponential backoff between retries.
   *
   * @param {Function} fn - Async function to execute
   * @param {number} retries - Number of retry attempts (default from constants)
   * @param {number} baseDelay - Base delay in ms between retries (default from constants)
   * @returns {Promise<*>} Result of the function
   */
  const withRetry = useCallback(
    async (fn, retries = API_RETRY_COUNT, baseDelay = API_RETRY_DELAY_MS) => {
      let lastError;

      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          return await fn();
        } catch (error) {
          lastError = error;

          // Don't retry on client errors (4xx) or specific business errors
          const isClientError =
            error.response?.status >= 400 && error.response?.status < 500;
          const isBusinessError =
            error.message?.includes('Activity log') ||
            error.message?.includes('not found') ||
            error.message?.includes('not authorized');

          if (isClientError || isBusinessError) {
            throw error;
          }

          // If not the last attempt, wait and retry
          if (attempt < retries - 1) {
            const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff
            console.warn(
              `[useSchedulerApi] Retry attempt ${attempt + 1}/${retries} after ${delay}ms`,
              error.message
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      // All retries exhausted
      throw lastError;
    },
    []
  );

  /**
   * Load schedule for a guard across a date range using the GetSchedulesByDateRange endpoint.
   * Includes retry logic for transient failures.
   *
   * @param {number} contactId - The guard's contact ID
   * @param {Date} startDate - Start of date range
   * @param {Date} endDate - End of date range
   * @returns {Promise<Array>} Array of schedule assignments
   */
  const loadScheduleByContact = useCallback(
    async (contactId, startDate, endDate) => {
      setLoading('patrols', true);
      try {
        const { data: patrols } = await withRetry(() =>
          getSchedulesByDateRange({
            startDate,
            endDate,
            contactId,
          })
        );

        return patrols;
      } catch (error) {
        console.error('[useSchedulerApi] Failed to load schedule:', error);
        notifyError('Failed to load schedule. Please try again.');
        throw error;
      } finally {
        setLoading('patrols', false);
      }
    },
    [notifyError, setLoading, withRetry]
  );

  /**
   * Load schedules for a company (and optionally a specific address) across a date range.
   * Includes retry logic for transient failures.
   *
   * @param {number} customerId - The company's customer ID
   * @param {Date} startDate - Start of date range
   * @param {Date} endDate - End of date range
   * @param {number} [addressId] - Optional address ID to filter by
   * @returns {Promise<Array>} Array of schedule assignments
   */
  const loadScheduleByCompany = useCallback(
    async (customerId, startDate, endDate, addressId = null) => {
      setLoading('patrols', true);
      try {
        const params = {
          startDate,
          endDate,
          customerId,
        };
        if (addressId) {
          params.addressId = addressId;
        }

        const { data: patrols } = await withRetry(() => getSchedulesByDateRange(params));

        return patrols;
      } catch (error) {
        console.error('[useSchedulerApi] Failed to load schedule by company:', error);
        notifyError('Failed to load schedule. Please try again.');
        throw error;
      } finally {
        setLoading('patrols', false);
      }
    },
    [notifyError, setLoading, withRetry]
  );

  /**
   * Loads initial scheduler data (guards and companies).
   * Includes retry logic for transient failures.
   *
   * @returns {Promise<{guards: Array, companies: Array}>}
   */
  const loadInitialData = useCallback(async () => {
    setLoading('initial', true);
    try {
      const [guardsData, companiesData] = await Promise.all([
        withRetry(() => fetchGuards()),
        withRetry(() => fetchCompanies()),
      ]);
      return { guards: guardsData, companies: companiesData };
    } catch (error) {
      console.error('[useSchedulerApi] Failed to load initial data:', error);
      notifyError('Failed to load scheduler data. Please refresh the page.');
      throw error;
    } finally {
      setLoading('initial', false);
    }
  }, [notifyError, setLoading, withRetry]);

  /**
   * Save a single patrol via the granular V4 API.
   * Creates a new patrol or updates an existing one without affecting other patrols.
   * Also handles rescheduled patrols from shift pushback (updates each independently).
   *
   * @param {Object} patrol - The patrol data to save
   * @param {Array} _existingPatrols - Unused (kept for backward-compatible call signature)
   * @param {Array} rescheduledPatrols - Patrols with updated times from shift pushback
   * @returns {Promise<Object>} Created or updated patrol data from the API
   */
  const savePatrol = useCallback(
    async (patrol, _existingPatrols = [], rescheduledPatrols = []) => {
      setLoading('saving', true);
      try {
        let result;

        if (patrol.ScheduleAssignmentId) {
          // Update existing patrol — only send changed fields
          result = await withRetry(() =>
            updateSinglePatrol(patrol.ScheduleAssignmentId, {
              CustomerId: patrol.CustomerId,
              AddressId: patrol.AddressId,
              ContactId: patrol.ContactId,
              StartTime: patrol.StartTime,
              EndTime: patrol.EndTime,
              Type: patrol.Type,
              Checklists: patrol.Checklists,
            })
          );
        } else {
          // Create new patrol
          result = await withRetry(() => createSinglePatrol(patrol));
        }

        // Apply rescheduled patrol times (from shift pushback) — update each independently
        if (rescheduledPatrols.length > 0) {
          try {
            const rescheduledPromises = rescheduledPatrols
              .filter((p) => p.ScheduleAssignmentId) // Only update existing patrols
              .map((p) =>
                withRetry(() =>
                  updateSinglePatrol(p.ScheduleAssignmentId, {
                    StartTime: p.StartTime,
                    EndTime: p.EndTime,
                  })
                )
              );
            await Promise.all(rescheduledPromises);
          } catch (rescheduledError) {
            console.error('[useSchedulerApi] Failed to update rescheduled patrols:', rescheduledError);
            notifyWarning(
              'Patrol saved, but some shifted patrol times could not be updated. Please review the schedule.'
            );
            return result;
          }
        }

        notifySuccess('Patrol saved successfully');
        return result;
      } catch (error) {
        console.error('[useSchedulerApi] Failed to save patrol:', error);

        if (error.message && error.message.includes('Activity log')) {
          notifyError(
            'Cannot save patrol: The location needs an activity log created first. Please contact an administrator.'
          );
        } else {
          notifyError(error.message || 'Failed to save patrol. Please try again.');
        }
        throw error;
      } finally {
        setLoading('saving', false);
      }
    },
    [notifySuccess, notifyError, setLoading, withRetry]
  );

  /**
   * Delete a single patrol via the granular V4 API.
   * Removes the patrol and all associated data (checklists, comment_list, etc.)
   * without affecting any other patrols.
   *
   * @param {Object} patrol - The patrol to delete (must have ScheduleAssignmentId)
   * @returns {Promise<void>}
   */
  const deletePatrol = useCallback(
    async (patrol) => {
      setLoading('deleting', true);
      try {
        await withRetry(() => deleteSinglePatrol(patrol.ScheduleAssignmentId));
        notifySuccess('Patrol deleted successfully');
      } catch (error) {
        console.error('[useSchedulerApi] Failed to delete patrol:', error);
        notifyError(error.message || 'Failed to delete patrol. Please try again.');
        throw error;
      } finally {
        setLoading('deleting', false);
      }
    },
    [notifySuccess, notifyError, setLoading, withRetry]
  );

  /**
   * Bulk create patrols via the V4 bulk API (for duplicate shift/week operations).
   * Each patrol is created independently — does NOT use full-replacement.
   * Supports partial success (some may fail while others succeed).
   *
   * @param {Array} patrols - Array of new patrol objects to create
   * @param {Array} _existingPatrols - Unused (kept for backward-compatible call signature)
   * @returns {Promise<Object>} Bulk create result with created patrols and any errors
   */
  const bulkSavePatrols = useCallback(
    async (patrols, _existingPatrols = []) => {
      setLoading('saving', true);
      try {
        const result = await withRetry(() => bulkCreatePatrols(patrols));

        if (result.totalFailed > 0) {
          notifyWarning(
            `${result.totalCreated} patrol(s) saved, ${result.totalFailed} failed. Check console for details.`
          );
        } else {
          notifySuccess(`${result.totalCreated} patrol(s) saved successfully`);
        }

        return result.created || [];
      } catch (error) {
        console.error('[useSchedulerApi] Failed to bulk save patrols:', error);

        if (error.message && error.message.includes('Activity log')) {
          notifyError(
            'Cannot save patrols: One or more locations need an activity log created first. Please contact an administrator.'
          );
        } else {
          notifyError(error.message || 'Failed to save patrols. Please try again.');
        }
        throw error;
      } finally {
        setLoading('saving', false);
      }
    },
    [notifySuccess, notifyWarning, notifyError, setLoading, withRetry]
  );

  /**
   * Check for conflicts in a bulk save operation.
   *
   * @param {Array} newPatrols - Array of new patrol objects to check
   * @param {Array} existingPatrols - All patrols currently loaded in the scheduler
   * @returns {Object|null} Conflict summary or null if no conflicts
   */
  const checkBulkConflicts = useCallback((newPatrols, existingPatrols = []) => {
    const conflicts = detectBulkConflicts(newPatrols, existingPatrols);
    return formatConflictSummary(conflicts);
  }, []);

  /**
   * Delete an entire shift (all patrols within the shift) via the granular V4 API.
   * Deletes each patrol independently without affecting other patrols.
   *
   * @param {Array} shiftPatrols - Array of patrol objects in the shift to delete
   * @param {Array} _existingPatrols - Unused (kept for backward-compatible call signature)
   * @returns {Promise<void>}
   */
  const deleteShift = useCallback(
    async (shiftPatrols, _existingPatrols = []) => {
      setLoading('deleting', true);
      try {
        const patrolsToDelete = shiftPatrols.filter((p) => p.ScheduleAssignmentId);
        const deletePromises = patrolsToDelete.map((p) =>
          withRetry(() => deleteSinglePatrol(p.ScheduleAssignmentId))
        );

        await Promise.all(deletePromises);
        notifySuccess(`${patrolsToDelete.length} patrol(s) deleted successfully`);
      } catch (error) {
        console.error('[useSchedulerApi] Failed to delete shift:', error);
        notifyError(error.message || 'Failed to delete shift. Please try again.');
        throw error;
      } finally {
        setLoading('deleting', false);
      }
    },
    [notifySuccess, notifyError, setLoading, withRetry]
  );

  /**
   * Change the guard assigned to all patrols in a shift via the granular V4 API.
   * Deletes patrols from the old guard and creates new ones for the new guard.
   *
   * @param {Array} shiftPatrols - Array of patrol objects to reassign
   * @param {Object} newGuard - The new guard object { id, name }
   * @param {Array} _existingPatrols - Unused (kept for backward-compatible call signature)
   * @returns {Promise<Array>} Created patrol data from the API
   */
  const changeShiftGuard = useCallback(
    async (shiftPatrols, newGuard, _existingPatrols = []) => {
      setLoading('saving', true);
      try {
        // Step 1: Delete all patrols from the old guard
        const deletePromises = shiftPatrols
          .filter((p) => p.ScheduleAssignmentId)
          .map((p) => withRetry(() => deleteSinglePatrol(p.ScheduleAssignmentId)));

        await Promise.all(deletePromises);

        // Step 2: Create new patrols for the new guard via bulk create
        const newPatrols = shiftPatrols.map((patrol) => ({
          CustomerId: patrol.CustomerId,
          AddressId: patrol.AddressId,
          ContactId: newGuard.id,
          StartTime: patrol.StartTime,
          EndTime: patrol.EndTime,
          Type: patrol.Type,
          Checklists: patrol.Checklists || [],
        }));

        const result = await withRetry(() => bulkCreatePatrols(newPatrols));

        if (result.totalFailed > 0) {
          notifyWarning(
            `${result.totalCreated} patrol(s) reassigned to ${newGuard.name}, ${result.totalFailed} failed.`
          );
        } else {
          notifySuccess(`${shiftPatrols.length} patrol(s) reassigned to ${newGuard.name}`);
        }

        return result.created || [];
      } catch (error) {
        console.error('[useSchedulerApi] Failed to change shift guard:', error);
        notifyError(error.message || 'Failed to change guard. Please try again.');
        throw error;
      } finally {
        setLoading('saving', false);
      }
    },
    [notifySuccess, notifyWarning, notifyError, setLoading, withRetry]
  );

  return {
    // Granular loading states
    loadingState,
    // Legacy isLoading for backward compatibility
    isLoading,
    // API methods
    loadInitialData,
    loadScheduleByContact,
    loadScheduleByCompany,
    savePatrol,
    deletePatrol,
    bulkSavePatrols,
    checkBulkConflicts,
    deleteShift,
    changeShiftGuard,
    // Notification methods
    notifySuccess,
    notifyError,
    notifyWarning,
  };
};

export default useSchedulerApi;
